import { type ComponentType, type LazyExoticComponent, lazy } from 'react'
import * as Sentry from '@sentry/react'

import { safeStorageGet, safeStorageSet, safeStorageRemove } from '../utils/safeStorage'

export type LazyComponentWithPreload<T extends ComponentType<any>> = {
  preload: () => Promise<{ default: T }>
} & LazyExoticComponent<T>

const RELOAD_FLAG = 'chunk_load_reload'
const MAX_RETRIES = 2
const RETRY_DELAYS_MS = [300, 1500]

type Factory = () => Promise<{ default: ComponentType<any> }>

const reportChunkLoadFailure = (error: unknown, recovery: 'reload' | 'failed'): void => {
  Sentry.captureException(error, {
    tags: { source: 'lazy_chunk_load', recovery },
    extra: { page: window.location.pathname },
  })
}

async function importWithRetry(factory: Factory, attempt = 0): Promise<{ default: ComponentType<any> }> {
  try {
    const module = await factory()
    // Re-arm the one-time reload for future stale-chunk failures in this session
    safeStorageRemove(RELOAD_FLAG)
    return module
  } catch (error) {
    if (attempt < MAX_RETRIES) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]))
      return importWithRetry(factory, attempt + 1)
    }

    if (!safeStorageGet(RELOAD_FLAG)) {
      safeStorageSet(RELOAD_FLAG, '1')
      reportChunkLoadFailure(error, 'reload')
      window.location.reload()
      return { default: (() => null) as unknown as ComponentType<any> }
    }

    safeStorageRemove(RELOAD_FLAG)
    reportChunkLoadFailure(error, 'failed')
    throw error
  }
}

const lazyWithPreload = (
  factory: Factory,
): LazyComponentWithPreload<ComponentType<any>> => {
  const retryFactory = () => importWithRetry(factory)
  const Component = lazy(retryFactory) as LazyComponentWithPreload<ComponentType<any>>
  Component.preload = retryFactory
  return Component
}

export const EntryPoint = lazyWithPreload(() => import('../../pages/EntryPoint'))
export const Welcome = lazyWithPreload(() => import('../../pages/Welcome'))

export const Map = lazyWithPreload(() => import('../../pages/Map'))
export const Searching = lazyWithPreload(() => import('../../pages/Map/pages/Searching'))
export const Warning = lazyWithPreload(() => import('../../pages/Map/pages/Warning'))
export const Validating = lazyWithPreload(() => import('../../pages/Map/pages/Validating'))

export const Lookup = lazyWithPreload(() => import('../../pages/Lookup'))

export const Result = lazyWithPreload(() => import('../../pages/Result'))
export const ReportReady = lazyWithPreload(() => import('../../pages/ReportReady'))
export const Signup = lazyWithPreload(() => import('../../pages/Signup'))
export const EmailConsent = lazyWithPreload(() => import('../../pages/EmailConsent'))

export const Checkout = lazyWithPreload(() => import('../../pages/Checkout'))
export const Paycheck1Pr1Default = lazyWithPreload(
  () => import('../../pages/Checkout/paycheck1_pr1_default'),
)
export const Success = lazyWithPreload(() => import('../../pages/Success'))

export const Terms = lazyWithPreload(() => import('../../pages/docs/Terms'))
export const Policy = lazyWithPreload(() => import('../../pages/docs/Policy'))
